/**
 * /solo/course/:courseId — SOLO course/units browse.
 *
 *  Shows all 36 units in a course with their 5 lessons, marked with completion status.
 *  Click a lesson → opens the lesson viewer.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Loader2, ArrowLeft, Check, Lock, ChevronRight } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const SoloCourse = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pacing, setPacing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, e] = await Promise.all([
        api.get(`/academy/solo/course/${encodeURIComponent(courseId)}/units`, { headers: authHeaders() }),
        api.get('/academy/solo/enrollment', { headers: authHeaders() }).catch(() => ({ data: {} })),
      ]);
      setUnits(u.data.units || []);
      setPacing(e.data?.pacing || null);
    } catch (er) {
      toast.error(er?.response?.data?.detail || 'Could not load course');
    } finally { setLoading(false); }
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
    </div>
  );

  const maxUnlocked = pacing?.max_unlocked_lesson ?? 179;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-16 pb-24" data-testid="solo-course">
      <div className="border-b-2 border-zinc-900 py-6 px-6">
        <div className="container mx-auto max-w-4xl">
          <Link to="/solo/athlete" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-3">
            <ArrowLeft className="w-3 h-3" /> Athlete Dashboard
          </Link>
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500">Training Room</div>
          <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tight leading-[1.05] mt-2">
            {units[0]?.course_id ? units[0].course_id.replace('solo::grade7::', '').replace('_', ' ').toUpperCase() : 'Course'}
          </h1>
          <div className="text-[11px] text-zinc-500 mt-2">{units.length} units · 5 lessons each</div>
        </div>
      </div>

      <div className="container mx-auto max-w-4xl px-6 py-8 space-y-4">
        {units.map((u) => (
          <div key={u.id} className="border-2 border-zinc-900 bg-zinc-950" data-testid={`unit-${u.order}`}>
            <div className="px-4 py-3 border-b-2 border-zinc-900 flex items-center gap-3">
              <div className="w-10 h-10 border-2 border-orange-500/40 flex items-center justify-center text-orange-500 font-black text-sm">
                {u.order}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-black text-white truncate">{u.title}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">
                  Unit {u.order} · {(u.lessons || []).length} lessons
                </div>
              </div>
            </div>
            <div className="divide-y divide-zinc-900">
              {(u.lessons || []).map((l) => {
                const locked = l.global_index > maxUnlocked;
                const done = l.status === 'completed';
                return (
                  <button key={l.id} disabled={locked}
                    onClick={() => navigate(`/solo/lesson/${encodeURIComponent(l.id)}`)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${locked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-zinc-900/60'}`}
                    data-testid={`lesson-row-${l.id}`}>
                    {locked ? <Lock className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" /> :
                     done ? <Check className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" /> :
                     <span className="w-3.5 h-3.5 rounded-full border border-zinc-700 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-black truncate ${done ? 'text-orange-500' : 'text-white'}`}>{l.title}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">{l.minutes_est || 20}m</div>
                    {!locked && <ChevronRight className="w-4 h-4 text-zinc-600" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SoloCourse;
